import { EmptyState } from "@arcadeai/design-system/components/ui/molecules/empty-state";
import {
  McpServerCard,
  type McpServerCardSelectionState,
} from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/server-card";
import { type ReactNode, useMemo } from "react";
import type { ColumnBreakpoint } from "../hooks/use-element-columns";
import { useIdCallback } from "../hooks/use-id-callback";
import { countSelectedTools } from "../selection";
import type { McpServer, Mode } from "../types";
import { VirtualizedCardGrid } from "./virtualized-card-grid";

// Server card (~104px) + buffer; the virtualizer measures actual heights.
const SERVER_CARD_ROW_HEIGHT = 116;
// Mirror McpServerPickerGrid's @container steps (@md=448, @3xl=768, @5xl=1024).
const COLUMN_BREAKPOINTS: ColumnBreakpoint[] = [
  { minWidth: 1024, columns: 4 },
  { minWidth: 768, columns: 3 },
  { minWidth: 448, columns: 2 },
];

function cardSelection(
  selectedCount: number,
  toolCount: number
): McpServerCardSelectionState {
  if (selectedCount === 0) {
    return "none";
  }
  return selectedCount >= toolCount ? "selected" : "partial";
}

const serverKey = (server: McpServer): string => server.name;

export function ServerGrid({
  emptyAction,
  emptyTitle = "No MCP servers match",
  mode,
  onDrillIn,
  onToggle,
  scrollContainer = null,
  selected,
  servers,
  trailingCard,
}: {
  emptyAction?: ReactNode;
  emptyTitle?: string;
  mode: Mode;
  onDrillIn: (name: string) => void;
  onToggle: (serverName: string) => void;
  scrollContainer?: HTMLElement | null;
  selected: Set<string>;
  servers: McpServer[];
  /** Card-sized cell rendered after the server cards (e.g. the unoptimized card). */
  trailingCard?: ReactNode;
}) {
  const serverNames = useMemo(() => servers.map((s) => s.name), [servers]);
  // Stable, id-bound handlers so the memoized cards re-render only when their
  // own selection count changes — not on every parent render.
  const getToggle = useIdCallback(onToggle, serverNames);
  const getDrillIn = useIdCallback(onDrillIn, serverNames);
  // Per-server selected counts, derived from the single `selected` set so the
  // card's selection state can't drift from it. Cards get a primitive count, so
  // only the server whose count changed re-renders on a toggle.
  const selectedCounts = useMemo(
    () =>
      new Map(
        servers.map((s) => [s.name, countSelectedTools(s.tools, selected)])
      ),
    [servers, selected]
  );
  const sections = useMemo(
    () => [{ id: "servers", items: servers }],
    [servers]
  );

  if (servers.length === 0 && !trailingCard) {
    return <EmptyState action={emptyAction} role="status" title={emptyTitle} />;
  }

  const renderServer = (server: McpServer) => {
    const toolCount = server.tools.length;
    const selectedCount = selectedCounts.get(server.name) ?? 0;
    const commonProps = {
      description: server.description,
      name: server.displayName,
      selectedToolCount: selectedCount,
      selectionState: cardSelection(selectedCount, toolCount),
      toolCount,
      toolkitId: server.name,
      version: server.version,
    };
    return mode === "single" ? (
      <McpServerCard
        {...commonProps}
        interaction="drill"
        onCustomize={getDrillIn(server.name)}
      />
    ) : (
      <McpServerCard
        {...commonProps}
        interaction="toggle"
        onCustomize={getDrillIn(server.name)}
        onToggle={getToggle(server.name)}
      />
    );
  };

  return (
    <VirtualizedCardGrid
      cardRowHeight={SERVER_CARD_ROW_HEIGHT}
      columnBreakpoints={COLUMN_BREAKPOINTS}
      getItemKey={serverKey}
      label="MCP servers"
      renderItem={renderServer}
      scrollContainer={scrollContainer}
      sections={sections}
      trailingCard={trailingCard}
    />
  );
}
